import {
  Checkbox,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import type { ReactNode } from "react";
import type { LidarClearanceAxis, LidarClearanceColorScale } from "../schemas";
import t from "../translations";
import { axisValueOptions, valueOptionAriaLabel, type LabelNamesByAxis } from "./axisValueLabels";
import type { WizardEntityCheck, WizardRiskTier } from "./clearanceRulesConfig";
import RemoveIconButton from "./RemoveIconButton";
import ValueOptionContent from "./ValueOptionContent";

type Props = {
  check: WizardEntityCheck;
  axes: LidarClearanceAxis[];
  labelNames: LabelNamesByAxis;
  axisColorScales: Map<number, LidarClearanceColorScale>;
  onChange: (check: WizardEntityCheck) => void;
  onRemove: () => void;
};

const TIER_FIELD_WIDTH = 340;

function TierLevelSelect({
  label,
  hint,
  value,
  optionItems,
  includeSameAsLevel,
  onChange,
}: {
  label: string;
  hint: string;
  value: number | "";
  optionItems: ReactNode;
  includeSameAsLevel?: boolean;
  onChange: (value: number | null) => void;
}) {
  return (
    <TextField
      select
      size="small"
      label={label}
      helperText={hint}
      value={value}
      onChange={(event) =>
        onChange(event.target.value === "" ? null : Number(event.target.value))
      }
      sx={{ width: TIER_FIELD_WIDTH }}
    >
      {includeSameAsLevel && <MenuItem value="">{t.SameAsLevelOption}</MenuItem>}
      {optionItems}
    </TextField>
  );
}

function TierFields({
  label,
  hint,
  tier,
  axis,
  labelNames,
  colorScale,
  onChange,
}: {
  label: string;
  hint: string;
  tier: WizardRiskTier | null;
  axis: LidarClearanceAxis | undefined;
  labelNames: LabelNamesByAxis;
  colorScale: LidarClearanceColorScale | undefined;
  onChange: (tier: WizardRiskTier | null) => void;
}) {
  const options = axis ? axisValueOptions(labelNames, axis, colorScale) : [];
  const optionItems = options.map((option) => (
    <MenuItem key={option.value} value={option.value} aria-label={valueOptionAriaLabel(option)}>
      <ValueOptionContent option={option} fullWidth />
    </MenuItem>
  ));

  return (
    <Stack spacing={0.5}>
      <FormControlLabel
        control={
          <Checkbox
            checked={tier !== null}
            onChange={(event) =>
              onChange(event.target.checked ? { level: null, partWillHitLevel: null } : null)
            }
          />
        }
        label={label}
      />
      <Typography variant="caption" color="text.secondary" sx={{ pl: 4, mt: -1 }}>
        {hint}
      </Typography>
      {tier !== null && (
        <Stack spacing={1.5} sx={{ pl: 4, pt: 0.5, alignItems: "flex-start" }}>
          <TierLevelSelect
            label={t.RiskTierLevelLabel}
            hint={t.RiskTierLevelHint}
            value={tier.level ?? ""}
            optionItems={optionItems}
            onChange={(level) => onChange({ ...tier, level })}
          />
          <TierLevelSelect
            label={t.RiskTierPartWillHitLevelLabel}
            hint={t.RiskTierPartWillHitLevelHint}
            value={tier.partWillHitLevel ?? ""}
            optionItems={optionItems}
            includeSameAsLevel
            onChange={(partWillHitLevel) => onChange({ ...tier, partWillHitLevel })}
          />
        </Stack>
      )}
    </Stack>
  );
}

export default function EntityCheckCard({
  check,
  axes,
  labelNames,
  axisColorScales,
  onChange,
  onRemove,
}: Props) {
  const axis = axes.find((axis) => axis.name_en === check.axis);
  const linkedIsolatedAxis = axes.find((axis) => axis.name_en === check.isolatedAxis);
  const colorScale = axis ? axisColorScales.get(axis.id) : undefined;
  // Isolated points are scored on isolatedAxis (once set) rather than axis -
  // its severity options/labels/colors must come from that axis, not the
  // base one, or a level picked here could be out of range for (or
  // mislabeled/miscolored against) where it's actually going to land.
  const loneRiskTreeAxis = linkedIsolatedAxis ?? axis;
  const loneRiskTreeColorScale = loneRiskTreeAxis
    ? axisColorScales.get(loneRiskTreeAxis.id)
    : undefined;

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }} data-testid="entity-check-card">
      <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center", mb: 1 }}>
        <Typography variant="subtitle2">{t.TreeRiskCheckLabel}</Typography>
        <RemoveIconButton aria-label={t.RemoveEntityCheckButton} onClick={onRemove} />
      </Stack>

      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <TextField
          select
          size="small"
          label={t.AxisLabel}
          value={check.axis}
          onChange={(event) => onChange({ ...check, axis: event.target.value })}
          sx={{ width: 240 }}
        >
          {/* check.axis can reference an axis this system doesn't currently
              link (e.g. loaded from a hand-edited file, or copied from a
              system with an extra axis) - shown as-is rather than left blank,
              so this doesn't look unset and risk being silently overwritten
              by picking any axis from the list below. */}
          {axis === undefined && check.axis !== "" && (
            <MenuItem value={check.axis}>{check.axis}</MenuItem>
          )}
          {axes.map((option) => (
            <MenuItem key={option.id} value={option.name_en}>
              {option.name_en}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          size="small"
          label={t.IsolatedAxisLabel}
          value={check.isolatedAxis}
          onChange={(event) => onChange({ ...check, isolatedAxis: event.target.value })}
          disabled={check.loneRiskTree === null}
          sx={{ width: 240 }}
          slotProps={{
            select: {
              // displayEmpty: MUI's Select only invokes renderValue for an
              // empty ("") value when this is set - otherwise it renders
              // nothing at all for that value, regardless of renderValue.
              displayEmpty: true,
              // Left unset, show the axis it'll actually fall back to
              // (rather than a generic "same as axis" placeholder) so this
              // previews the real behavior at a glance.
              renderValue: (value: unknown) => (value === "" ? check.axis : (value as string)),
            },
            // An empty value otherwise keeps the label unshrunk (centered,
            // overlapping the renderValue text above) since MUI's shrink
            // heuristic only looks at the real (still-empty) value, not
            // what displayEmpty/renderValue end up showing.
            inputLabel: { shrink: true },
          }}
        >
          <MenuItem value="">{t.SameAsAxisOption}</MenuItem>
          {/* Same "reference an axis this system doesn't currently link" case
              as the main axis field above. */}
          {linkedIsolatedAxis === undefined && check.isolatedAxis !== "" && (
            <MenuItem value={check.isolatedAxis}>{check.isolatedAxis}</MenuItem>
          )}
          {axes.map((option) => (
            <MenuItem key={option.id} value={option.name_en}>
              {option.name_en}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      <Stack spacing={3}>
        <TierFields
          label={t.RiskTreeTierLabel}
          hint={t.RiskTreeTierHint}
          tier={check.riskTree}
          axis={axis}
          labelNames={labelNames}
          colorScale={colorScale}
          onChange={(riskTree) => onChange({ ...check, riskTree })}
        />

        <TierFields
          label={t.LoneRiskTreeTierLabel}
          hint={t.LoneRiskTreeTierHint}
          tier={check.loneRiskTree}
          axis={loneRiskTreeAxis}
          labelNames={labelNames}
          colorScale={loneRiskTreeColorScale}
          onChange={(loneRiskTree) => onChange({ ...check, loneRiskTree })}
        />
      </Stack>
    </Paper>
  );
}
